import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/* ═══════════════════════════════════════════════════════════════════════
 * DataVaultModal — signed, tamper-evident data extraction portal.
 *
 * Two top-level modes:
 *   1. BULK EXTRACT — every operation's data for a date range, delivered
 *      as a ZIP (Level 1 download / Level 2 folder pick / Level 3 SFTP push).
 *   2. TARGETED QUERY — pick one operation, drill down to specific
 *      entities (vessels / trains / silos), pick specific metrics, get
 *      a purpose-built CSV. Every extract is signed + logged.
 *
 * Admin + Manager only — enforced backend-side too.
 * ═══════════════════════════════════════════════════════════════════════ */

function todayISO() { return new Date().toISOString().slice(0, 10); }
function daysAgo(n) {
    const d = new Date(); d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
}

const HAS_FS_PICKER = typeof window !== "undefined" &&
    typeof window.showDirectoryPicker === "function";

export default function DataVaultModal({ open, onClose }) {
    const [tab, setTab] = useState("bulk");          // bulk | targeted
    const [start, setStart] = useState(daysAgo(30));
    const [end, setEnd] = useState(todayISO());
    const [history, setHistory] = useState([]);

    // Bulk state
    const [sources, setSources] = useState([]);
    const [selected, setSelected] = useState([]);
    const [busy, setBusy] = useState(false);
    const [mode, setMode] = useState("download");    // download | folder | sftp
    const [sftp, setSftp] = useState({
        host: "", port: 22, username: "", password: "",
        remote_path: "/", filename: "",
    });

    // Targeted state
    const [catalog, setCatalog] = useState({});      // {opKey: {label, metrics:[{key,label}]}}
    const [tOp, setTOp] = useState("");
    const [tEntities, setTEntities] = useState([]);  // available entities
    const [tPickedEnts, setTPickedEnts] = useState([]);
    const [tMetrics, setTMetrics] = useState([]);    // selected metric keys
    const [tLoadingEnts, setTLoadingEnts] = useState(false);

    useEffect(() => {
        if (!open) return;
        api.get("/data-vault/sources").then((r) => {
            setSources(r.data || []);
            setSelected((r.data || []).map((s) => s.key));
        }).catch(() => setSources([]));
        api.get("/data-vault/query-catalog").then((r) => {
            setCatalog(r.data || {});
            const first = Object.keys(r.data || {})[0] || "";
            setTOp(first);
        }).catch(() => setCatalog({}));
        api.get("/data-vault/history?limit=10").then((r) => setHistory(r.data?.extracts || []))
            .catch(() => setHistory([]));
    }, [open]);

    // Load entities whenever targeted op changes
    useEffect(() => {
        if (!open || !tOp) return;
        setTLoadingEnts(true);
        setTPickedEnts([]);
        setTMetrics([]);
        api.get(`/data-vault/entities?operation=${encodeURIComponent(tOp)}`)
            .then((r) => setTEntities(r.data || []))
            .catch(() => setTEntities([]))
            .finally(() => setTLoadingEnts(false));
    }, [tOp, open]);

    const tOpMeta = useMemo(() => catalog[tOp] || null, [catalog, tOp]);

    if (!open) return null;

    const toggle = (list, setList, k) =>
        setList(list.includes(k) ? list.filter((x) => x !== k) : [...list, k]);

    const refreshHistory = async () => {
        try {
            const h = await api.get("/data-vault/history?limit=10");
            setHistory(h.data?.extracts || []);
        } catch { /* ignore */ }
    };

    const doDownload = async (writeToFolder = false) => {
        setBusy(true);
        try {
            const res = await api.post("/data-vault/extract",
                { start_date: start, end_date: end, operations: selected },
                { responseType: "blob" });
            const blob = res.data;
            const fname = `TTV_extract_${start}_${end}.zip`;
            if (writeToFolder && HAS_FS_PICKER) {
                const dir = await window.showDirectoryPicker({ mode: "readwrite" });
                const fh = await dir.getFileHandle(fname, { create: true });
                const w = await fh.createWritable();
                await w.write(blob); await w.close();
                toast.success(`Written to ${dir.name}/${fname}`);
            } else {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = fname; document.body.appendChild(a);
                a.click(); a.remove(); URL.revokeObjectURL(url);
                toast.success(`Downloaded ${fname}`);
            }
            await refreshHistory();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const doSftp = async () => {
        if (!sftp.host || !sftp.username || !sftp.password) {
            toast.error("SFTP host, username and password are required");
            return;
        }
        setBusy(true);
        try {
            const r = await api.post("/data-vault/extract/sftp", {
                extract: { start_date: start, end_date: end, operations: selected },
                sftp: {
                    host: sftp.host, port: Number(sftp.port) || 22,
                    username: sftp.username, password: sftp.password,
                    remote_path: sftp.remote_path || "/",
                    filename: sftp.filename || undefined,
                },
            });
            toast.success(`Uploaded to ${r.data.remote_path} · ${r.data.row_total} rows`);
            await refreshHistory();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const doTargeted = async (writeToFolder = false) => {
        if (!tOp) { toast.error("Pick an operation"); return; }
        if (tMetrics.length === 0) { toast.error("Pick at least one metric"); return; }
        setBusy(true);
        try {
            const res = await api.post("/data-vault/query", {
                operation: tOp,
                entity_ids: tPickedEnts,       // empty = all entities
                metrics: tMetrics,
                start_date: start,
                end_date: end,
            }, { responseType: "blob" });
            const blob = res.data;
            const rows = res.headers?.["x-row-total"] || "?";
            const fname = `TTV_${tOp}_${start}_${end}.csv`;
            if (writeToFolder && HAS_FS_PICKER) {
                const dir = await window.showDirectoryPicker({ mode: "readwrite" });
                const fh = await dir.getFileHandle(fname, { create: true });
                const w = await fh.createWritable();
                await w.write(blob); await w.close();
                toast.success(`Written to ${dir.name}/${fname} · ${rows} rows`);
            } else {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = fname; document.body.appendChild(a);
                a.click(); a.remove(); URL.revokeObjectURL(url);
                toast.success(`Downloaded ${fname} · ${rows} rows`);
            }
            await refreshHistory();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const dateRow = (
        <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
                <label className="uppercase-label block text-[10px] mb-1">Start date</label>
                <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                       className="g-input" data-testid="dv-start" />
            </div>
            <div>
                <label className="uppercase-label block text-[10px] mb-1">End date</label>
                <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                       className="g-input" data-testid="dv-end" />
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
             onClick={onClose} data-testid="data-vault-modal">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl my-8" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-4 py-3 bg-[#0B1A3B] text-white rounded-t-lg">
                    <div className="font-bold uppercase tracking-wide">Data Vault · Extract Portal</div>
                    <button onClick={onClose} className="px-2 py-1 hover:bg-white/10 rounded" data-testid="dv-close">✕</button>
                </div>

                {/* Top tabs: Bulk vs Targeted */}
                <div className="flex border-b bg-slate-50">
                    {[
                        { k: "bulk",     label: "Bulk extract" },
                        { k: "targeted", label: "Targeted query" },
                    ].map((t) => (
                        <button key={t.k} onClick={() => setTab(t.k)}
                                data-testid={`dv-tab-${t.k}`}
                                className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors
                                            ${tab === t.k
                                              ? "bg-white text-[#0B1A3B] border-b-2 border-[#0B1A3B]"
                                              : "text-slate-500 hover:text-slate-700"}`}>
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="p-4 space-y-4">
                    {dateRow}

                    {/* ══════════════════════════ BULK TAB ══════════════════════════ */}
                    {tab === "bulk" && (
                        <>
                            <div>
                                <div className="uppercase-label text-[10px] mb-1 flex items-center justify-between">
                                    <span>Operations to include ({selected.length}/{sources.length})</span>
                                    <div className="flex gap-2">
                                        <button onClick={() => setSelected(sources.map((s) => s.key))}
                                                data-testid="dv-select-all"
                                                className="text-blue-600 text-[10px] font-bold">Select all</button>
                                        <button onClick={() => setSelected([])}
                                                data-testid="dv-clear-all"
                                                className="text-red-600 text-[10px] font-bold">Clear</button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 max-h-56 overflow-y-auto p-2 border rounded bg-white">
                                    {sources.map((s) => (
                                        <label key={s.key} className="flex items-center gap-2 text-xs cursor-pointer text-slate-800 py-1 hover:bg-slate-50 rounded px-1">
                                            <input type="checkbox" checked={selected.includes(s.key)}
                                                   onChange={() => toggle(selected, setSelected, s.key)}
                                                   className="w-4 h-4 flex-shrink-0"
                                                   data-testid={`dv-src-${s.key}`} />
                                            <span className="text-slate-800 flex-1 truncate">{s.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Delivery mode tabs */}
                            <div className="border-t pt-3">
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {[
                                        { k: "download", label: "Download ZIP" },
                                        { k: "folder",   label: `To local folder${HAS_FS_PICKER ? "" : " (Chrome/Edge only)"}` },
                                        { k: "sftp",     label: "SFTP push" },
                                    ].map((t) => (
                                        <button key={t.k} onClick={() => setMode(t.k)}
                                                disabled={t.k === "folder" && !HAS_FS_PICKER}
                                                className={`px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider
                                                            ${mode === t.k ? "bg-[#0B1A3B] text-white" : "bg-slate-100 text-slate-700"}
                                                            disabled:opacity-40 disabled:cursor-not-allowed`}
                                                data-testid={`dv-mode-${t.k}`}>
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                                {mode === "sftp" && (
                                    <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 p-3 rounded border">
                                        <div><label className="uppercase-label block text-[10px] mb-1">Host</label>
                                            <input value={sftp.host} onChange={(e) => setSftp({ ...sftp, host: e.target.value })}
                                                   placeholder="sftp.grindrod.co.za" className="g-input" data-testid="dv-sftp-host" /></div>
                                        <div><label className="uppercase-label block text-[10px] mb-1">Port</label>
                                            <input type="number" value={sftp.port} onChange={(e) => setSftp({ ...sftp, port: e.target.value })}
                                                   className="g-input" data-testid="dv-sftp-port" /></div>
                                        <div><label className="uppercase-label block text-[10px] mb-1">Username</label>
                                            <input value={sftp.username} onChange={(e) => setSftp({ ...sftp, username: e.target.value })}
                                                   className="g-input" data-testid="dv-sftp-user" /></div>
                                        <div><label className="uppercase-label block text-[10px] mb-1">Password</label>
                                            <input type="password" value={sftp.password} onChange={(e) => setSftp({ ...sftp, password: e.target.value })}
                                                   className="g-input" data-testid="dv-sftp-pass" /></div>
                                        <div className="col-span-2"><label className="uppercase-label block text-[10px] mb-1">Remote path</label>
                                            <input value={sftp.remote_path} onChange={(e) => setSftp({ ...sftp, remote_path: e.target.value })}
                                                   placeholder="/reports/coal_tippler" className="g-input" data-testid="dv-sftp-path" /></div>
                                    </div>
                                )}
                            </div>

                            {/* Bulk actions */}
                            <div className="flex justify-end gap-2 border-t pt-3">
                                <button onClick={onClose} disabled={busy}
                                        className="px-4 py-2 rounded bg-slate-200 hover:bg-slate-300 text-sm font-bold">Cancel</button>
                                <button
                                    onClick={() => {
                                        if (mode === "download") doDownload(false);
                                        else if (mode === "folder") doDownload(true);
                                        else doSftp();
                                    }}
                                    disabled={busy || selected.length === 0}
                                    className="px-5 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-40"
                                    data-testid="dv-extract-btn">
                                    {busy ? "Working…" :
                                     mode === "download" ? "Extract & Download" :
                                     mode === "folder"   ? "Extract to folder" :
                                                           "Extract & Push"}
                                </button>
                            </div>
                        </>
                    )}

                    {/* ═════════════════════════ TARGETED TAB ═════════════════════════ */}
                    {tab === "targeted" && (
                        <>
                            <div className="text-[11px] text-slate-600 bg-blue-50 border border-blue-200 rounded p-2 leading-relaxed">
                                Pick one operation, drill down to specific vessels / trains / silos, then pick the metrics you want. Leave the entity list empty to include every entity in the range.
                            </div>

                            {/* Step 1 · Operation */}
                            <div>
                                <label className="uppercase-label block text-[10px] mb-1">1 · Operation</label>
                                <div className="flex flex-wrap gap-2">
                                    {Object.entries(catalog).map(([k, v]) => (
                                        <button key={k} onClick={() => setTOp(k)}
                                                data-testid={`dv-t-op-${k}`}
                                                className={`px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider transition-colors
                                                            ${tOp === k ? "bg-[#0B1A3B] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                                            {v.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Step 2 · Entities */}
                            {tOp && (
                                <div>
                                    <div className="uppercase-label text-[10px] mb-1 flex items-center justify-between">
                                        <span>2 · Targets ({tPickedEnts.length ? `${tPickedEnts.length} picked` : "all"})</span>
                                        <div className="flex gap-2">
                                            <button onClick={() => setTPickedEnts(tEntities.map((e) => e.id))}
                                                    data-testid="dv-t-select-all"
                                                    className="text-blue-600 text-[10px] font-bold">Select all</button>
                                            <button onClick={() => setTPickedEnts([])}
                                                    data-testid="dv-t-clear"
                                                    className="text-red-600 text-[10px] font-bold">Clear (= all)</button>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-48 overflow-y-auto p-2 border rounded bg-white">
                                        {tLoadingEnts && <div className="text-xs text-slate-500 p-2">Loading…</div>}
                                        {!tLoadingEnts && tEntities.length === 0 &&
                                            <div className="text-xs text-slate-500 p-2">No entities found for this operation.</div>}
                                        {!tLoadingEnts && tEntities.map((ent) => {
                                            const extras = Object.entries(ent.extra || {})
                                                .filter(([, v]) => v)
                                                .map(([k, v]) => `${k}: ${v}`).join(" · ");
                                            return (
                                                <label key={ent.id} className="flex items-center gap-2 text-xs cursor-pointer text-slate-800 py-1 hover:bg-slate-50 rounded px-1">
                                                    <input type="checkbox" checked={tPickedEnts.includes(ent.id)}
                                                           onChange={() => toggle(tPickedEnts, setTPickedEnts, ent.id)}
                                                           className="w-4 h-4 flex-shrink-0"
                                                           data-testid={`dv-t-ent-${ent.id}`} />
                                                    <span className="flex-1 truncate">
                                                        <span className="font-bold">{ent.label || ent.id}</span>
                                                        {extras && <span className="text-slate-500"> · {extras}</span>}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Step 3 · Metrics */}
                            {tOpMeta && (
                                <div>
                                    <div className="uppercase-label text-[10px] mb-1 flex items-center justify-between">
                                        <span>3 · Metrics ({tMetrics.length}/{tOpMeta.metrics.length})</span>
                                        <div className="flex gap-2">
                                            <button onClick={() => setTMetrics(tOpMeta.metrics.map((m) => m.key))}
                                                    data-testid="dv-t-metrics-all"
                                                    className="text-blue-600 text-[10px] font-bold">Select all</button>
                                            <button onClick={() => setTMetrics([])}
                                                    data-testid="dv-t-metrics-clear"
                                                    className="text-red-600 text-[10px] font-bold">Clear</button>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 p-2 border rounded bg-white">
                                        {tOpMeta.metrics.map((m) => (
                                            <label key={m.key} className="flex items-center gap-2 text-xs cursor-pointer text-slate-800 py-1 hover:bg-slate-50 rounded px-1">
                                                <input type="checkbox" checked={tMetrics.includes(m.key)}
                                                       onChange={() => toggle(tMetrics, setTMetrics, m.key)}
                                                       className="w-4 h-4 flex-shrink-0"
                                                       data-testid={`dv-t-metric-${m.key}`} />
                                                <span className="text-slate-800 flex-1 truncate">{m.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Targeted actions */}
                            <div className="flex justify-end gap-2 border-t pt-3">
                                <button onClick={onClose} disabled={busy}
                                        className="px-4 py-2 rounded bg-slate-200 hover:bg-slate-300 text-sm font-bold">Cancel</button>
                                {HAS_FS_PICKER && (
                                    <button onClick={() => doTargeted(true)}
                                            disabled={busy || !tOp || tMetrics.length === 0}
                                            className="px-4 py-2 rounded bg-slate-700 hover:bg-slate-800 text-white text-sm font-bold disabled:opacity-40"
                                            data-testid="dv-t-folder-btn">
                                        Save to folder
                                    </button>
                                )}
                                <button onClick={() => doTargeted(false)}
                                        disabled={busy || !tOp || tMetrics.length === 0}
                                        className="px-5 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-40"
                                        data-testid="dv-t-download-btn">
                                    {busy ? "Working…" : "Run query & download"}
                                </button>
                            </div>
                        </>
                    )}

                    {/* History (shared) */}
                    {history.length > 0 && (
                        <div className="border-t pt-3">
                            <div className="uppercase-label text-[10px] mb-2 text-slate-600">Recent Extracts (audit trail)</div>
                            <div className="space-y-2 max-h-56 overflow-y-auto">
                                {history.map((h) => (
                                    <div key={h.id} className="border border-slate-200 rounded p-2 bg-slate-50 text-[11px]">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="font-mono text-slate-700">{h.requested_at?.slice(0, 19)?.replace("T", " ")}</span>
                                            <div className="flex items-center gap-1">
                                                {h.query_type === "targeted" && (
                                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-purple-100 text-purple-700">Targeted</span>
                                                )}
                                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase
                                                    ${h.delivery === "sftp" ? "bg-blue-100 text-blue-700"
                                                    : h.delivery === "downloaded" ? "bg-emerald-100 text-emerald-700"
                                                    : "bg-red-100 text-red-700"}`}>
                                                    {h.delivery}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-slate-700">
                                            <div><span className="text-slate-500">By: </span><b>{h.requested_by_name}</b></div>
                                            <div><span className="text-slate-500">Rows: </span><b>{h.row_total}</b></div>
                                            <div className="col-span-2 font-mono text-[10px]">
                                                <span className="text-slate-500">Range: </span>{h.start_date} → {h.end_date}
                                            </div>
                                            {h.metrics && h.metrics.length > 0 && (
                                                <div className="col-span-2 text-[10px] text-slate-600">
                                                    <span className="text-slate-500">Metrics: </span>{h.metrics.join(", ")}
                                                </div>
                                            )}
                                            {h.manifest_sha256 && (
                                                <div className="col-span-2 font-mono text-[10px] text-slate-500 truncate" title={h.manifest_sha256}>
                                                    sha256: {h.manifest_sha256?.slice(0, 24)}…
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="text-[10px] text-slate-500 border-t pt-2">
                        Every extract is signed with a SHA-256 manifest and logged permanently for audit. Deletes are not permitted.
                    </div>
                </div>
            </div>
        </div>
    );
}
